'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Block, Button, Card, Page } from 'konsta/react';

declare global {
  interface Window {
    liff?: {
      init: (options: { liffId: string }) => Promise<void>;
      isInClient: () => boolean;
      isLoggedIn: () => boolean;
      login: (options?: { redirectUri?: string }) => void;
      getIDToken: () => string | null;
      permission?: {
        query: (permission: 'openid' | 'profile' | 'email' | 'chat_message.write') => Promise<{
          state: 'granted' | 'prompt' | 'unavailable';
        }>;
        requestAll?: () => Promise<void>;
      };
    };
  }
}

const LIFF_SDK_URL = 'https://static.line-scdn.net/liff/edge/2/sdk.js';

function loadLiffSdk() {
  if (window.liff) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${LIFF_SDK_URL}"]`);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('LINE LIFF SDK 載入失敗')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = LIFF_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('LINE LIFF SDK 載入失敗'));
    document.head.appendChild(script);
  });
}

function isMobileBrowser() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(window.navigator.userAgent);
}

type MiniAppAccessGateProps = {
  children: ReactNode;
  liffId?: string | null;
  devPreviewEnabled?: boolean;
  officialAccountUrl?: string | null;
  officialAccountQrCodeUrl?: string | null;
};

type MiniAppBlockedScreenProps = {
  message?: string;
  officialAccountUrl?: string | null;
  officialAccountQrCodeUrl?: string | null;
};

function MiniAppBlockedScreen({
  message = '請從 LINE 官方帳號或圖文選單開啟 MiniApp。',
  officialAccountUrl,
  officialAccountQrCodeUrl,
}: MiniAppBlockedScreenProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileBrowser());
  }, []);

  return (
    <Page>
      <Block>
        <Card header="請從 LINE 開啟">
        <p>{message}</p>

        {!isMobile && officialAccountQrCodeUrl && (
          <Block inset>
            <img src={officialAccountQrCodeUrl} alt="官方 LINE 帳號 QRCode" />
            <span>使用手機 LINE 掃描後，從官方帳號進入 MiniApp。</span>
          </Block>
        )}

        {isMobile && officialAccountUrl && (
          <Button href={officialAccountUrl} large rounded>
            開啟官方 LINE 帳號
          </Button>
        )}
        </Card>
      </Block>
    </Page>
  );
}

export function MiniAppUnavailableScreen() {
  return (
    <main className="access-shell">
      <section className="access-panel">
        <div className="service-unavailable-icon" aria-hidden="true">—</div>
        <h1>目前沒有可用服務</h1>
        <p>此商店尚未啟用 MiniApp 課程預約服務，請聯絡店家確認。</p>
      </section>
    </main>
  );
}

// 僅記住同一份文件內成功的 LINE 啟動檢查，不持久保存，也不替代伺服器會員授權。
const allowedLaunches = new Set<string>();

export function MiniAppAccessGate(props: MiniAppAccessGateProps) {
  const launchKey = JSON.stringify([props.liffId, Boolean(props.devPreviewEnabled)]);
  return <MiniAppLaunchGate key={launchKey} {...props} launchKey={launchKey} />;
}

function MiniAppLaunchGate({
  launchKey,
  children,
  liffId,
  devPreviewEnabled = false,
  officialAccountUrl,
  officialAccountQrCodeUrl,
}: MiniAppAccessGateProps & { launchKey: string }) {
  const [state, setState] = useState<'checking' | 'allowed' | 'blocked'>(() => allowedLaunches.has(launchKey) ? 'allowed' : 'checking');
  const [message, setMessage] = useState('正在確認 LINE MiniApp 啟動狀態...');
  const didRunRef = useRef(false);

  useEffect(() => {
    if (allowedLaunches.has(launchKey) || didRunRef.current) {
      return;
    }

    didRunRef.current = true;

    async function verifyLiffLaunch() {
      try {
        if (devPreviewEnabled) {
          if (liffId) {
            let isLineClient = false;
            try {
              await loadLiffSdk();
              isLineClient = Boolean(window.liff?.isInClient());
              if (isLineClient && window.liff) {
                await window.liff.init({ liffId });
              }
            } catch (error) {
              if (isLineClient || /Line\//i.test(window.navigator.userAgent)) {
                throw error;
              }
            }
          }
          allowedLaunches.add(launchKey);
          setState('allowed');
          return;
        }

        if (!liffId) {
          setMessage('尚未設定 LIFF ID，無法啟動 MiniApp。');
          setState('blocked');
          return;
        }

        await loadLiffSdk();

        if (!window.liff) {
          setMessage('LINE LIFF SDK 尚未初始化。');
          setState('blocked');
          return;
        }

        if (!window.liff.isInClient()) {
          setMessage('請從 LINE 官方帳號或圖文選單開啟 MiniApp。');
          setState('blocked');
          return;
        }

        await window.liff.init({ liffId });
        allowedLaunches.add(launchKey);
        setState('allowed');
      } catch (error) {
        setMessage(error instanceof Error ? error.message : '請從 LINE 官方帳號開啟 MiniApp。');
        setState('blocked');
      }
    }

    void verifyLiffLaunch();
  }, [devPreviewEnabled, liffId, launchKey]);

  if (state === 'allowed') {
    return <>{children}</>;
  }

  if (state === 'checking') {
    return (
      <main className="access-shell">
        <section className="access-panel">
          <div className="app-mark">LINE</div>
          <h1>正在開啟 MiniApp</h1>
          <p>{message}</p>
          <div className="loading-bar" />
        </section>
      </main>
    );
  }

  return (
    <MiniAppBlockedScreen
      message={message}
      officialAccountUrl={officialAccountUrl}
      officialAccountQrCodeUrl={officialAccountQrCodeUrl}
    />
  );
}
